import React, { useEffect, useState } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  MenuItem,
  Box,
  IconButton,
  Typography,
} from "@mui/material";
import { Close } from "@mui/icons-material";
import ButtonOne from "../../common/normal-button";
import ButtonTwo from "../../common/dashboard-button";

const SOURCE_OPTIONS = ["RedVision", "iWell", "Bonanza"];

const EMPTY_FORM = {
  date: "",
  source: "",
  branch_name: "",
  advisor_name: "",
  investment: "",
  portfolio_value: "",
  balance_units: "",
  avg_holding_days: "",
  divident: "",
  divident_reinvested: "",
  gain_loss: "",
  absolute_return: "",
  xirr: "",
};

const DailyHoldingFormDialog = ({ open, handleClose, onSave, editData, saving = false }) => {
  const [form, setForm] = useState(EMPTY_FORM);

  useEffect(() => {
    if (editData) {
      setForm({
        date:                editData.date                ?? "",
        source:              editData.source              ?? "",
        branch_name:         editData.branch_name         ?? "",
        advisor_name:        editData.advisor_name        ?? "",
        investment:          editData.investment          ?? "",
        portfolio_value:     editData.portfolio_value     ?? "",
        balance_units:       editData.balance_units       ?? "",
        avg_holding_days:    editData.avg_holding_days    ?? "",
        divident:            editData.divident            ?? "",
        divident_reinvested: editData.divident_reinvested ?? "",
        gain_loss:           editData.gain_loss           ?? "",
        absolute_return:     editData.absolute_return     ?? "",
        xirr:                editData.xirr                ?? "",
      });
    } else {
      setForm(EMPTY_FORM);
    }
  }, [editData, open]);

  const set = (field, value) => setForm((prev) => ({ ...prev, [field]: value }));

  const handleSave = () => {
    const payload = { ...form };
    // Send nulls for empty numeric fields so backend accepts them
    const numericFields = [
      "investment", "portfolio_value", "balance_units", "avg_holding_days",
      "divident", "divident_reinvested", "gain_loss", "absolute_return", "xirr",
    ];
    numericFields.forEach((f) => {
      if (payload[f] === "" || payload[f] === null) payload[f] = null;
    });
    onSave(payload);
  };

  return (
    <Dialog open={open} onClose={handleClose} fullWidth maxWidth="sm">
      <DialogTitle sx={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
        Edit Holding
        <IconButton onClick={handleClose} disabled={saving}>
          <Close />
        </IconButton>
      </DialogTitle>

      <DialogContent>
        <Box display="flex" flexDirection="column" gap={2} mt={1}>

          <Typography variant="caption" color="text.secondary" fontWeight={600} sx={{ textTransform: "uppercase", letterSpacing: 0.5, mb: -1 }}>
            Identity
          </Typography>

          <Box display="flex" gap={2}>
            <TextField label="PAN" value={editData?.pan ?? ""} fullWidth disabled />
            <TextField label="Client Code" value={editData?.client_code ?? ""} fullWidth disabled />
          </Box>

          <Box display="flex" gap={2}>
            <TextField
              label="Date"
              type="date"
              value={form.date}
              onChange={(e) => set("date", e.target.value)}
              fullWidth
              InputLabelProps={{ shrink: true }}
            />
            <TextField
              select
              label="Source"
              value={form.source}
              onChange={(e) => set("source", e.target.value)}
              fullWidth
            >
              {SOURCE_OPTIONS.map((s) => (
                <MenuItem key={s} value={s}>{s}</MenuItem>
              ))}
            </TextField>
          </Box>

          <Typography variant="caption" color="text.secondary" fontWeight={600} sx={{ textTransform: "uppercase", letterSpacing: 0.5, mb: -1 }}>
            Branch &amp; Advisor
          </Typography>

          <Box display="flex" gap={2}>
            <TextField
              label="Branch Name"
              value={form.branch_name}
              onChange={(e) => set("branch_name", e.target.value)}
              fullWidth
            />
            <TextField
              label="Advisor Name"
              value={form.advisor_name}
              onChange={(e) => set("advisor_name", e.target.value)}
              fullWidth
            />
          </Box>

          <Typography variant="caption" color="text.secondary" fontWeight={600} sx={{ textTransform: "uppercase", letterSpacing: 0.5, mb: -1 }}>
            Portfolio
          </Typography>

          <Box display="flex" gap={2}>
            <TextField
              label="Investment"
              value={form.investment}
              onChange={(e) => set("investment", e.target.value)}
              fullWidth
              type="number"
            />
            <TextField
              label="Portfolio Value"
              value={form.portfolio_value}
              onChange={(e) => set("portfolio_value", e.target.value)}
              fullWidth
              type="number"
            />
          </Box>

          <Box display="flex" gap={2}>
            <TextField
              label="Balance Units"
              value={form.balance_units}
              onChange={(e) => set("balance_units", e.target.value)}
              fullWidth
              type="number"
            />
            <TextField
              label="Avg. Holding Days"
              value={form.avg_holding_days}
              onChange={(e) => set("avg_holding_days", e.target.value)}
              fullWidth
              type="number"
            />
          </Box>

          <Typography variant="caption" color="text.secondary" fontWeight={600} sx={{ textTransform: "uppercase", letterSpacing: 0.5, mb: -1 }}>
            Returns
          </Typography>

          <Box display="flex" gap={2}>
            <TextField
              label="Dividend"
              value={form.divident}
              onChange={(e) => set("divident", e.target.value)}
              fullWidth
              type="number"
            />
            <TextField
              label="Dividend Reinvested"
              value={form.divident_reinvested}
              onChange={(e) => set("divident_reinvested", e.target.value)}
              fullWidth
              type="number"
            />
          </Box>

          <Box display="flex" gap={2}>
            <TextField
              label="Gain / Loss"
              value={form.gain_loss}
              onChange={(e) => set("gain_loss", e.target.value)}
              fullWidth
              type="number"
            />
            <TextField
              label="Absolute Return (%)"
              value={form.absolute_return}
              onChange={(e) => set("absolute_return", e.target.value)}
              fullWidth
              type="number"
            />
          </Box>

          <TextField
            label="XIRR (%)"
            value={form.xirr}
            onChange={(e) => set("xirr", e.target.value)}
            fullWidth
            type="number"
          />

        </Box>
      </DialogContent>

      <DialogActions sx={{ p: 2, px: 3 }}>
        <ButtonTwo onClick={handleClose} disabled={saving}>Cancel</ButtonTwo>
        <ButtonOne onClick={handleSave} disabled={saving}>
          {saving ? "Saving…" : "Update"}
        </ButtonOne>
      </DialogActions>
    </Dialog>
  );
};

export default DailyHoldingFormDialog;
